// Short-lived cache for rarely-changing reference lists fetched through the Vuex `fetch` action
// (categories, units, suppliers, packages). Pages used to re-request these on every navigation.
//
// - Opt-in by exact url (TTL map below); search/paginated urls are never cached.
// - Scoped per token + store + branch, so a new login or branch switch never sees old data.
// - Concurrent requests for the same url share one in-flight promise.
// - Any successful non-GET to the same resource (e.g. POST categories, categories_import)
//   invalidates it — see invalidateForMutation(), called from the axios response interceptor.
const TTL_MS = {
  categories: 5 * 60 * 1000,
  product_size: 5 * 60 * 1000,
  suppliers: 5 * 60 * 1000,
  packages: 5 * 60 * 1000,
};

const entries = new Map(); // key -> { at, data }
const inflight = new Map(); // key -> Promise

const keyOf = (scope, url) => `${scope}|${url}`;
const clone = (v) => (v === undefined ? v : JSON.parse(JSON.stringify(v)));

export const isCacheable = (url) => Object.prototype.hasOwnProperty.call(TTL_MS, url);

export const scopeOf = (token, user) =>
  `${token || ""}:${(user && user.stores && user.stores.id) || ""}:${
    (user && user.branch_id) || ""
  }`;

// loader: () => Promise<data | undefined>. Returns a private copy so callers can mutate it.
export const cachedFetch = async (scope, url, loader) => {
  const key = keyOf(scope, url);
  const hit = entries.get(key);
  if (hit && Date.now() - hit.at < TTL_MS[url]) return clone(hit.data);

  let pending = inflight.get(key);
  if (!pending) {
    pending = loader()
      .then((data) => {
        if (data !== undefined) entries.set(key, { at: Date.now(), data });
        return data;
      })
      .finally(() => inflight.delete(key));
    inflight.set(key, pending);
  }
  return clone(await pending);
};

export const invalidateForMutation = (url) => {
  const seg = String(url || "").replace(/^\/+/, "").split(/[/?]/)[0];
  if (!seg) return;
  Object.keys(TTL_MS).forEach((base) => {
    if (seg === base || seg.startsWith(`${base}_`)) {
      Array.from(entries.keys()).forEach((k) => {
        if (k.endsWith(`|${base}`)) entries.delete(k);
      });
      Array.from(inflight.keys()).forEach((k) => {
        if (k.endsWith(`|${base}`)) inflight.delete(k);
      });
    }
  });
};

// Clock of the last successful write (POST/PUT/PATCH/DELETE). Kept-alive pages compare it with
// the time they were left to decide whether their data may have changed (see keepAlive.js).
let lastMutationAt = 0;
export const markMutation = () => {
  lastMutationAt = Date.now();
};
export const getLastMutationAt = () => lastMutationAt;

// ---------------------------------------------------------------------------------------------
// HTTP-level GET cache (used by plugins/axios.js for `$http.get`). Unlike the store cache above
// this covers list endpoints whose data changes with ANY write (stock moves with sales, edits,
// imports...), so an entry is only reused if no write completed since it was requested.
// Survives layout changes (a kept-alive page does not), so table <-> products is instant.
const GET_RULES = [
  { test: (p) => p === "products_all", ttl: 60 * 1000 },
  { test: (p) => p === "get_summary", ttl: 60 * 1000 },
  { test: (p) => p === "table_zones", ttl: 60 * 1000 },
  // Re-run on every layout mount (default.vue / drawerRight.vue), i.e. on each layout switch.
  { test: (p) => p === "me", ttl: 60 * 1000 },
  { test: (p) => p === "has_multiple_role", ttl: 5 * 60 * 1000 },
  { test: (p) => p === "list_order", ttl: 30 * 1000 },
  // sale page product grid: short TTL because stock moves with every sale on any device
  { test: (p) => p === "products_paginate", ttl: 30 * 1000 },
  { test: (p) => p === "categories_paginate", ttl: 60 * 1000 },
  { test: (p) => p === "suppliers_paginate", ttl: 60 * 1000 },
  { test: (p) => p === "product_size_paginate", ttl: 60 * 1000 },
];

const pathOf = (url) =>
  String(url || "").replace(/^\/+/, "").split("?")[0];

export const getCacheRule = (url) => {
  const p = pathOf(url);
  return GET_RULES.find((r) => r.test(p)) || null;
};

const responses = new Map(); // key -> { startedAt, at, res }
const requestsInflight = new Map(); // key -> Promise<res>

// loader: () => Promise<axiosResponse>. Hits return a copy whose data is private to the caller.
export const cachedRequest = async (scope, url, config, rule, loader) => {
  const key = `${scope}|${pathOf(url)}?${JSON.stringify(
    (config && config.params) || {}
  )}${url.includes("?") ? `&${String(url).split("?")[1]}` : ""}`;
  const out = (res) => ({ ...res, data: clone(res.data) });

  const hit = responses.get(key);
  if (
    hit &&
    Date.now() - hit.at < rule.ttl &&
    getLastMutationAt() < hit.startedAt
  ) {
    return out(hit.res);
  }

  let pending = requestsInflight.get(key);
  if (!pending) {
    const startedAt = Date.now();
    pending = loader()
      .then((res) => {
        if (res && res.data && res.data.status !== false) {
          responses.set(key, { startedAt, at: Date.now(), res });
        }
        return res;
      })
      .finally(() => requestsInflight.delete(key));
    requestsInflight.set(key, pending);
  }
  return out(await pending);
};
